import { useCallback } from 'react';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useSeedLessonShellCache from '@/data/hook/lesson-progress/useSeedLessonShellCache';
import useNeighbors from '@/data/hook/shared/useNeighbors';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { studentDashboardQueries } from '@/data/query-keys/studentDashboardQueryKey';
import LessonProgressDashboardStats from '@/models/LessonProgressDashboardStats';
import {
  courseLessonProgressFilters,
  LESSON_NEIGHBORS_PAGE_SIZE,
} from '@/utils/lesson-progress/courseLessonProgressFilters';
import toLessonNeighbors from '@/utils/lesson-progress/toLessonNeighbors';
import { ROUTES } from '@/utils/routes';

type UseLessonNeighborsParams = {
  courseProgressId?: number;
  lessonId: number;
  slug?: string;
  klassId?: string;
};

export default function useLessonNeighbors({
  courseProgressId,
  lessonId,
  slug,
  klassId,
}: UseLessonNeighborsParams) {
  const {
    results: lessonProgresses,
    isInitialLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteService<LessonProgressDashboardStats>({
    ...studentDashboardQueries.lessonProgressesInfinite(
      courseLessonProgressFilters(courseProgressId, LESSON_NEIGHBORS_PAGE_SIZE),
    ),
    enabled: !!courseProgressId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  useSeedLessonShellCache({ courseProgressId, lessonProgresses });

  const resolve = useCallback(
    (items: LessonProgressDashboardStats[]) =>
      toLessonNeighbors(items, lessonId),
    [lessonId],
  );

  const { previous, next, isLoading } = useNeighbors({
    items: lessonProgresses,
    isInitialLoading,
    hasNextPage,
    fetchNextPage,
    resolve,
  });

  const toLessonHref = (neighbor?: LessonProgressDashboardStats) =>
    slug && neighbor
      ? ROUTES.COURSES.LESSON.INFO(slug, neighbor.lesson.id, klassId)
      : undefined;

  return {
    previousHref: toLessonHref(previous),
    nextHref: toLessonHref(next),
    isLoading,
  };
}
